import {
  type EventPayload,
  type RunState,
  type TriageCategory,
  explainCategory,
  triageQuestions,
} from '@orgsim/shared';
import { useEffect, useRef, useState } from 'react';
import { CATEGORY_COLORS, CATEGORY_TEXT_COLORS } from '../../signs/svg';
import { Icon } from '../Icon';
import { CATEGORY_LABELS, TRANSFER_DESTINATIONS } from '../labels';

type Props = {
  state: RunState;
  readOnly: boolean;
  focusRequest: { id: string } | null;
  onlyFocused?: boolean;
  onLocate: (patientId: string) => void;
  limitedResources: boolean;
  dispatch: (p: EventPayload) => void;
};

function Decision({
  label,
  value,
  onSet,
}: {
  label: string;
  value: boolean | undefined;
  onSet: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex-1">{label}</span>
      <div className="flex overflow-hidden rounded border border-neutral-700">
        {[true, false].map((option) => (
          <button
            key={String(option)}
            type="button"
            aria-pressed={value === option}
            onClick={() => onSet(option)}
            className={`px-2 py-0.5 text-xs ${
              value === option ? 'bg-red-600 text-white' : 'bg-neutral-800 hover:bg-neutral-700'
            }`}
          >
            {option ? 'Ja' : 'Nein'}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PatientsPanel({
  state,
  readOnly,
  focusRequest,
  onlyFocused = false,
  onLocate,
  limitedResources,
  dispatch,
}: Props) {
  const [open, setOpen] = useState<string | null>(focusRequest?.id ?? null);
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => setOpen(focusRequest?.id ?? null), [focusRequest]);
  useEffect(() => {
    if (open)
      listRef.current?.querySelector('[data-open="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [open]);
  const patients = Object.values(state.patients)
    .filter((p) => !onlyFocused || p.id === focusRequest?.id)
    .sort((a, b) => a.number - b.number);

  return (
    <ul className="space-y-2" ref={listRef}>
      {patients.length === 0 ? (
        <li className="panel-description">In dieser Lage sind noch keine Patienten angelegt.</li>
      ) : null}
      {patients.map((p) => {
        const Heading = onlyFocused ? 'div' : 'button';
        const mismatch = p.category && p.category !== p.expectedCategory;
        // Whether the "Nach der Einordnung" box will actually show anything. SK II/III always
        // carry a fixed guidance paragraph; SK I's content depends on decisions the candidate
        // may not have made yet, and on !readOnly the Decision controls themselves count as
        // content. Read-only with nothing decided (yet) must not render an empty, heading-only
        // box - the same problem already fixed for SK IV/V by leaving the box out entirely.
        const hasGuidance =
          p.category === 'II' ||
          p.category === 'III' ||
          (p.category === 'I' &&
            (!readOnly ||
              p.surgicalIndication === false ||
              (p.surgicalIndication === true && p.transportReleased !== undefined)));
        return (
          <li key={p.id} data-open={open === p.id} className="rounded border border-neutral-700">
            <Heading
              type={onlyFocused ? undefined : 'button'}
              onClick={onlyFocused ? undefined : () => setOpen(open === p.id ? null : p.id)}
              aria-expanded={onlyFocused ? undefined : open === p.id}
              className="patient-heading flex w-full items-center gap-3 text-left"
            >
              <span
                className="patient-number"
                style={{
                  background: p.category ? CATEGORY_COLORS[p.category] : '#41464d',
                  color: p.category ? CATEGORY_TEXT_COLORS[p.category] : '#fff',
                }}
              >
                {p.number}
              </span>
              <span className="flex-1">
                <span className="block font-medium">Patient {p.number}</span>
                <span className="block text-xs text-neutral-400 mt-1">
                  {p.summary || 'Ohne Kurzbeschreibung'}
                </span>
              </span>
              {p.destination ? <span className="text-xs text-neutral-500">verlegt</span> : null}
              {mismatch ? <span className="text-xs text-red-300">Abweichung</span> : null}
              {!onlyFocused ? (
                <Icon
                  name="chevron"
                  size={14}
                  style={{ rotate: open === p.id ? '90deg' : '0deg' }}
                />
              ) : null}
            </Heading>
            {open === p.id ? (
              <div className="patient-content space-y-3 border-t border-neutral-700">
                {!readOnly ? (
                  <div className="triage-options" aria-label="Sichtungskategorie">
                    {(Object.keys(CATEGORY_LABELS) as TriageCategory[]).map((c) => (
                      <button
                        key={c}
                        type="button"
                        aria-pressed={p.category === c}
                        aria-label={CATEGORY_LABELS[c]}
                        title={CATEGORY_LABELS[c]}
                        onClick={() =>
                          dispatch({ type: 'PatientTriaged', patientId: p.id, category: c })
                        }
                        className={p.category === c ? 'triage-option selected' : 'triage-option'}
                        style={{
                          background: CATEGORY_COLORS[c],
                          color: CATEGORY_TEXT_COLORS[c],
                        }}
                      >
                        <span>SK</span>
                        <strong>{c}</strong>
                      </button>
                    ))}
                  </div>
                ) : null}
                {p.category ? (
                  <div className="text-xs">
                    Ist: {CATEGORY_LABELS[p.category]}
                    {mismatch ? <span className="text-red-300"> (weicht vom Soll ab)</span> : null}
                  </div>
                ) : null}
                <button type="button" className="button quiet" onClick={() => onLocate(p.id)}>
                  <Icon name="target" size={15} />
                  Auf Karte zeigen
                </button>
                <div>
                  <div className="mb-1 text-xs uppercase tracking-wide text-neutral-500">
                    Sichtung, Frage für Frage
                  </div>
                  <ol className="space-y-1">
                    {triageQuestions(p.triage, limitedResources).map((row) => (
                      <li key={row.field} className="flex gap-2">
                        <span className="flex-1 text-neutral-400">{row.question}</span>
                        <span className="font-medium">{row.answer}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                {p.findings.length > 0 ? (
                  <div>
                    <div className="mb-1 text-xs uppercase tracking-wide text-neutral-500">
                      Weitere Befunde
                    </div>
                    <ol className="space-y-1">
                      {p.findings.map((f, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: order is the identity
                        <li key={i} className="flex gap-2">
                          <span className="w-1/2 text-neutral-400">{f.question}</span>
                          <span className="w-1/2">{f.answer}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : null}
                <details className="instructor-answer">
                  <summary>Soll und Ausbilderhinweis</summary>
                  <div className="mt-2">Soll: {CATEGORY_LABELS[p.expectedCategory]}</div>
                  <div className="text-amber-100/70">
                    {explainCategory(p.triage, limitedResources).reason}
                  </div>
                  {p.instructorNote ? (
                    <div className="mt-1 text-amber-100">{p.instructorNote}</div>
                  ) : null}
                </details>
                {hasGuidance ? (
                  <div className="space-y-2 rounded border border-neutral-800 p-2">
                    <div className="text-xs uppercase tracking-wide text-neutral-500">
                      Nach der Einordnung
                    </div>
                    {p.category === 'I' ? (
                      <>
                        {!readOnly ? (
                          <Decision
                            label="Akute OP-Indikation"
                            value={p.surgicalIndication}
                            onSet={(indicated) =>
                              dispatch({
                                type: 'SurgicalIndicationSet',
                                patientId: p.id,
                                indicated,
                              })
                            }
                          />
                        ) : null}
                        {p.surgicalIndication === false ? (
                          <p className="text-xs text-neutral-400">
                            Ohne OP-Indikation sieht die Vorgabe die Rückstufung nach SK II vor.
                          </p>
                        ) : null}
                        {p.surgicalIndication ? (
                          <>
                            {!readOnly ? (
                              <Decision
                                label="Transportfreigabe LNA/OrgL"
                                value={p.transportReleased}
                                onSet={(released) =>
                                  dispatch({
                                    type: 'TransportReleaseSet',
                                    patientId: p.id,
                                    released,
                                  })
                                }
                              />
                            ) : null}
                            <p className="text-xs text-neutral-400">
                              {p.transportReleased === undefined
                                ? null
                                : p.transportReleased
                                  ? 'Vorgabe: sofortiger Transport.'
                                  : 'Vorgabe: Schwerstverletzten-Behandlungsplatz.'}
                            </p>
                          </>
                        ) : null}
                      </>
                    ) : null}
                    {p.category === 'II' ? (
                      <p className="text-xs text-neutral-400">
                        Vorgabe: Schwerstverletzten-Behandlungsplatz, Sichtung durch den Triagearzt,
                        Transport nach Dringlichkeit.
                      </p>
                    ) : null}
                    {p.category === 'III' ? (
                      <p className="text-xs text-neutral-400">
                        Vorgabe: Leichtverletzten-Behandlungsplatz, danach Vitalparameter und
                        erneute Untersuchung. Kritischer Befund bei der Nachuntersuchung:{' '}
                        {p.triage.criticalOnReassessment ? 'ja' : 'nein'}.{' '}
                        {p.triage.criticalOnReassessment
                          ? 'Vorgabe: Verlegung auf den Schwerstverletzten-Behandlungsplatz.'
                          : 'Vorgabe: Transport nach Verfügbarkeit.'}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {!readOnly ? (
                  <div className="flex flex-wrap gap-1">
                    {TRANSFER_DESTINATIONS.map((destination) => (
                      <button
                        key={destination}
                        type="button"
                        onClick={() =>
                          dispatch({ type: 'PatientTransferred', patientId: p.id, destination })
                        }
                        className="rounded bg-neutral-800 px-2 py-1 text-xs hover:bg-neutral-700"
                      >
                        {destination}
                      </button>
                    ))}
                  </div>
                ) : null}
                {!readOnly ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const dest = new FormData(e.currentTarget).get('dest');
                      if (typeof dest === 'string' && dest.trim())
                        dispatch({
                          type: 'PatientTransferred',
                          patientId: p.id,
                          destination: dest.trim(),
                        });
                      e.currentTarget.reset();
                    }}
                    className="flex gap-1"
                  >
                    <input
                      name="dest"
                      aria-label="Verlegungsziel"
                      placeholder="Verlegen nach (Ablage, Klinik)"
                      className="flex-1 rounded bg-neutral-800 px-2 py-1"
                    />
                    <button type="submit" className="rounded bg-neutral-800 px-2 py-1">
                      Verlegen
                    </button>
                  </form>
                ) : null}
                {p.destination ? (
                  <div className="text-xs text-neutral-400">Verlegt nach {p.destination}</div>
                ) : null}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
